"use client";

import { type FormEvent, useState, useEffect } from "react";
import { Phone } from "lucide-react";
import { Heading } from "../design-system/atoms/Heading";
import { Button } from "../design-system/atoms/Button";
import { Checkbox } from "../design-system/atoms/Checkbox";
import { Input } from "../design-system/atoms/Input";
import Textarea from "../design-system/atoms/Textarea";
import { cn } from "../design-system/lib/utils";
import type { BlockProps } from "./blocks/registry";
import { geoService, type Prediction } from "../services/geoService";
import { useGeo } from "../context/GeoContext";
import CallPopup from "./callBack";
import { useFormValidation } from "../hooks/useFormValidation";
import { FORM_FIELDS } from "../utils/formFields";
import {
  checked,
  email as emailValidator,
  minLength,
  personName,
  phone as phoneValidator,
  required,
} from "../utils/validation";

// Fonction utilitaire de parsing d'adresse partagée
const parseAddress = async (addressText: string, context: string = "") => {
  console.log(`🔍 Début parsing adresse (${context}):`, addressText);

  const trimmedAddress = addressText.trim();

  // Regex pour trouver le code postal (5 chiffres) et la ville qui suit
  // OU gérer les adresses sans code postal: "rue, ville, pays"
  const addressPatternWithPostcode = /^(.+?)\s*,?\s*(\d{5})\s+(.+)$/;
  const addressPatternWithoutPostcode = /^(.+?)\s*,\s*([^,]+)\s*,?\s*(.+)?$/;

  let match = trimmedAddress.match(addressPatternWithPostcode);
  let hasPostcode = true;

  if (!match) {
    // Essayer le pattern sans code postal
    match = trimmedAddress.match(addressPatternWithoutPostcode);
    hasPostcode = false;
    console.log(`🔍 Pattern sans code postal utilisé (${context})`);
  }

  console.log(`🔍 DEBUG REGEX (${context}):`, {
    addressText: trimmedAddress,
    match,
    hasPostcode
  });

  let street = "";
  let postcode = "";
  let city = "";

  if (match) {
    if (hasPostcode) {
      // Pattern avec code postal: rue + code postal + ville
      console.log(`✅ Pattern avec code postal trouvé (${context})`);
      street = match[1].trim().replace(/,\s*$/, '');
      postcode = match[2];
      city = match[3].split(',')[0].trim();
    } else {
      // Pattern sans code postal: rue + ville + pays
      console.log(`✅ Pattern sans code postal trouvé (${context})`);
      street = match[1].trim().replace(/,\s*$/, '');
      city = match[2].trim();

      // Récupérer le code postal avec l'API data.gouv.fr
      console.log(`🔍 Tentative récupération code postal avec API data.gouv.fr (${context})...`);
      try {
        const query = `${street} ${city}`.replace(/\s+/g, '+');
        const response = await fetch(`https://api-adresse.data.gouv.fr/search/?q=${query}&limit=1`);
        const data = await response.json();

        if (data.features && data.features.length > 0) {
          const feature = data.features[0];
          postcode = feature.properties.postcode || '';
          console.log(`✅ Code postal récupéré via API data.gouv.fr (${context}):`, postcode);
        } else {
          console.log(`⚠️ Aucun résultat trouvé via API data.gouv.fr (${context})`);
        }
      } catch (error) {
        console.warn(`⚠️ Erreur récupération code postal via API data.gouv.fr (${context}):`, error);
      }
    }
    console.log(`📍 VALEURS EXTRAITES (${context}):`, { street, postcode, city });
  } else {
    // Fallback: chercher juste le code postal
    const postcodeMatch = trimmedAddress.match(/\b(\d{5})\b/);
    if (postcodeMatch && postcodeMatch.index !== undefined) {
      postcode = postcodeMatch[1];
      street = trimmedAddress.substring(0, postcodeMatch.index).trim();
      city = trimmedAddress.substring(postcodeMatch.index + 5).trim();
    } else {
      // Si aucun code postal trouvé, utiliser l'API data.gouv.fr
      console.log(`⚠️ Pattern NON trouvé - tentative API data.gouv.fr (${context})...`);
      try {
        const query = trimmedAddress.replace(/\s+/g, '+');
        const response = await fetch(`https://api-adresse.data.gouv.fr/search/?q=${query}&limit=1`);
        const data = await response.json();

        if (data.features && data.features.length > 0) {
          const feature = data.features[0];
          street = feature.properties.name || trimmedAddress;
          postcode = feature.properties.postcode || '';
          city = feature.properties.city || '';
          console.log(`🔧 Fallback API data.gouv.fr réussi (${context}):`, { street, postcode, city });
        }
      } catch (error) {
        console.warn(`⚠️ Fallback API data.gouv.fr a échoué (${context}):`, error);
      }
    }
  }

  return { street, postcode, city };
};

const FIELD_ICONS = {
  nom: `https://site-lms.fra1.digitaloceanspaces.com/user_45eec5a949.svg`,
  prenom: `https://site-lms.fra1.digitaloceanspaces.com/user_45eec5a949.svg`,
  email: `https://site-lms.fra1.digitaloceanspaces.com/Email_black_b9614703cb.svg`,
  telephone: `https://site-lms.fra1.digitaloceanspaces.com/Phone_black_3e770a13cf.svg`,
  adresse: `https://site-lms.fra1.digitaloceanspaces.com/Mail_black_6acf0d9eb5.svg`,
  message: `https://site-lms.fra1.digitaloceanspaces.com/Message_e699b86cd5.svg`,
} as const;



type ContactBlockData = {
  id?: number | string;
  documentId?: string;
  titre?: string;
};

type ContactFormProps = {
  id?: number;
  className?: string;
  onSubmit?: (event: FormEvent<HTMLFormElement>) => void;
} & BlockProps<ContactBlockData>;

export default function ContactForm({
  id = 5789,
  className,
  onSubmit,
  data,
}: ContactFormProps) {
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<
    "idle" | "success" | "error"
  >("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const [gdprChecked, setGdprChecked] = useState(false);
  const [addressQuery, setAddressQuery] = useState("");

  // Encart "appel immédiat" affiché quand l'agence de l'adresse a showIdelioButton
  const [showIdelioBox, setShowIdelioBox] = useState(false);
  const [callbackChecked, setCallbackChecked] = useState(false);
  const [showCallPopup, setShowCallPopup] = useState(false);

  const { setAgency, setHasSelected } = useGeo();
  const [predictions, setPredictions] = useState<Prediction[]>([]);
  const [showPredictions, setShowPredictions] = useState(false);
  const [, setSelectedPlaceId] = useState("");
  const [, setIsLoadingAddress] = useState(false);

  const [addressDetails, setAddressDetails] = useState<{
    street: string;
    postcode: string;
    city: string;
    idSalesforce: string;
  }>({ street: "", postcode: "", city: "", idSalesforce: "" });

  // État pour stocker les valeurs parsées finales utilisées dans le formulaire
  const [parsedAddress, setParsedAddress] = useState({
    street: "",
    postcode: "",
    city: ""
  });

  const { errors, validateField, validateAll, clearErrors } = useFormValidation({
    lastname: [
      required("Veuillez saisir votre nom."),
      personName("Veuillez saisir un nom valide (au moins 2 lettres)."),
    ],
    firstname: [
      required("Veuillez saisir votre prénom."),
      personName("Veuillez saisir un prénom valide (au moins 2 lettres)."),
    ],
    email: [
      required("Veuillez saisir votre adresse e-mail."),
      emailValidator(),
    ],
    phone: [
      required("Veuillez saisir votre téléphone."),
      phoneValidator("Veuillez saisir un numéro de téléphone français valide."),
    ],
    adresse: [required("Veuillez saisir votre adresse de livraison.")],
    description: [
      required("Veuillez saisir votre message."),
      minLength(10, "Votre message doit contenir au moins 10 caractères."),
    ],
    consent_gdpr: [
      checked("Vous devez accepter l'utilisation de vos données."),
    ],
    consent_email: [
      checked("Vous devez valider votre autorisation de contact par e-mail."),
    ],
  });

  // DEBUG: Logger les valeurs d'adresse quand elles changent
  useEffect(() => {
    console.log('📤 CHAMPS CACHÉS CONTACT - street:', addressDetails.street, '| city:', addressDetails.city, '| postcode:', addressDetails.postcode);
  }, [addressDetails]);

  const subject = "Contactez-nous"; // Sujet par défaut, sera modifié dans handleSubmit

  // Fetch address predictions
  useEffect(() => {
    const handler = setTimeout(async () => {
      if (addressQuery.length >= 3) {
        try {
          const data = await geoService.getPredictions(addressQuery);
          const addresses = data.filter((d: Prediction) =>
            d.types.some((t) =>
              ["subpremise", "street_address", "geocode"].includes(t)
            )
          );
          setPredictions(addresses);
          setShowPredictions(true);
        } catch (err) {
          console.error("Erreur API predictions:", err);
        }
      } else {
        setPredictions([]);
        setShowPredictions(false);
      }
    }, 500);

    return () => clearTimeout(handler);
  }, [addressQuery]);

  if (!data) return null;

  const getAgencyDetails = async (placeId: string) => {
    try {
      const agency = await geoService.getMap(placeId);

      if (agency && agency.id) {
        return agency;
      }

      return await geoService.getNearestAgency(placeId);
    } catch (error) {
      console.error("Erreur getMap/getNearestAgency:", error);
      return null;
    }
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSubmit?.(event);

    // Store form reference before async operations
    const form = event.currentTarget;
    const formData = new FormData(form);

    // Validation de tous les champs avant envoi
    const isValid = validateAll({
      lastname: (formData.get("lastname") as string) || "",
      firstname: (formData.get("firstname") as string) || "",
      email: (formData.get(FORM_FIELDS.EMAIL) as string) || "",
      phone: (formData.get(FORM_FIELDS.PHONE) as string) || "",
      adresse: addressQuery,
      description: (formData.get(FORM_FIELDS.MESSAGE) as string) || "",
      consent_gdpr: gdprChecked,
      consent_email: formData.get(FORM_FIELDS.CONSENT_EMAIL) === "on",
    });
    if (!isValid) {
      setSubmitStatus("idle");
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus("idle");
    setErrorMessage("");

    // DEBUG: Log au début du handleSubmit
    console.log('🚀 DÉBUT HANDLE SUBMIT (Contact):');
    console.log('  - Page actuelle:', window.location.pathname);
    console.log('  - addressQuery initial:', addressQuery);
    console.log('  - addressDetails initial:', addressDetails);

    // Get UTM parameters and gclid from URL
    const urlParams = new URLSearchParams(window.location.search);
    const utmSource = urlParams.get("utm_source");
    const utmCampaign = urlParams.get("utm_campaign");
    const utmContent = urlParams.get("utm_content");
    const utmTerm = urlParams.get("utm_term");
    const utmMedium = urlParams.get("utm_medium");
    const gclid = urlParams.get("gclid");


    // Extraire les valeurs des champs du formulaire
    // Extraire depuis FormData avec tous les noms possibles
    const firstname = (formData.get('firstname') || formData.get(FORM_FIELDS.FIRSTNAME || '') || '') as string;
    const lastname = (formData.get('lastname') || formData.get(FORM_FIELDS.LASTNAME || '') || '') as string;
    const phone = formData.get(FORM_FIELDS.PHONE) as string;
    let subject = formData.get(FORM_FIELDS.SUBJECT) as string;

    // Déterminer le type de contact selon la page actuelle
    const getContactType = () => {
      const pathname = window.location.pathname;

      if (pathname.includes('/convive')) {
        return 'Convive';
      } else if (pathname.includes('/pour-un-proche')) {
        return 'Pour un proche';
      } else {
        return 'Accueil';
      }
    };

    // Modifier le sujet pour correspondre au format demandé
    const contactType = getContactType();
    subject = `Formulaire de contact [${contactType}] - ${firstname} ${lastname}`;

    console.log('📍 Type de contact détecté:', contactType, 'pour pathname:', window.location.pathname);
    const email = formData.get(FORM_FIELDS.EMAIL) as string;
    const message = formData.get(FORM_FIELDS.MESSAGE) as string;
    const consentGdpr = gdprChecked || formData.get(FORM_FIELDS.CONSENT_GDPR) === "on";
    const consentEmail = formData.get(FORM_FIELDS.CONSENT_EMAIL) === "on";

    // Utiliser les données d'adresse extraites lors de la sélection
    console.log('🔍 DEBUG - addressDetails initial:', addressDetails);
    console.log('🔍 DEBUG - addressQuery:', addressQuery);

    let street = addressDetails.street;
    let postcode = addressDetails.postcode;
    let city = addressDetails.city;

    //const idSalesforce = "001AW00000tIcAQYA0"; // ID agence fixé en dur
    const idSalesforce = addressDetails.idSalesforce;

    console.log('📍 ADRESSE - Données de l\'API:', { street, postcode, city });
    console.log('📍 ADRESSE - addressQuery:', addressQuery);

    // Si les données d'adresse sont manquantes, parser addressQuery avec la fonction utilitaire
    if (!street || !city || !postcode) {
      console.log('⚠️ Données d\'adresse manquantes, parsing de addressQuery...');
      console.log('🔍 ÉTAT INITIAL - street:', street, '| city:', city, '| postcode:', postcode);
      console.log('🔍 VALEUR ADDRESSQUERY AVANT PARSING:', JSON.stringify(addressQuery));
      console.log('🔍 VALEUR ADDRESSDETAILS AVANT PARSING:', JSON.stringify(addressDetails));

      // Utiliser la fonction utilitaire de parsing
      const parsedAddress = await parseAddress(addressQuery, "Contact");
      street = parsedAddress.street;
      postcode = parsedAddress.postcode;
      city = parsedAddress.city;

      console.log('📍 VALEURS EXTRAITES (Contact) - street:', street, '| postcode:', postcode, '| city:', city);
    }

    console.log('✅ ADRESSE FINALE À ENVOYER:', { street, postcode, city, idSalesforce });
    console.log('📤 ENVOI À STRAPI - Adresse Contact:', { street, postcode, city });

    // DEBUG: Comparer adresse saisie vs adresse récupérée
    console.log('🔍 COMPARAISON ADRESSES:');
    console.log('  - Adresse saisie (addressQuery):', addressQuery);
    console.log('  - Adresse récupérée (street):', street);
    console.log('  - Correspondance ?', addressQuery.includes(street) || street.includes(addressQuery.split(',')[0]));

    // DEBUG: Vérifier le type de contact
    console.log('🎯 TYPE DE CONTACT DÉTECTÉ:', contactType);
    console.log('🎯 PAGE ACTUELLE:', window.location.pathname);

    // DEBUG: Vérifier les valeurs avant envoi
    console.log('🔍 VALEURS D ADRESSE AVANT ENVOI AU BACKEND:');
    console.log('  - street (type):', street, typeof street);
    console.log('  - postcode (type):', postcode, typeof postcode);
    console.log('  - city (type):', city, typeof city);
    console.log('  - idSalesforce (type):', idSalesforce, typeof idSalesforce);

    // Mettre à jour parsedAddress pour le formulaire HTML
    setParsedAddress({ street, postcode, city });

    console.log('🔄 PARSING TERMINÉ - parsedAddress mis à jour:', { street, postcode, city });

    // Prepare data for Strapi API
    const submissionData = {
      data: {
        // Informations personnelles
        firstname,
        lastname,
        phone,
        email,
        subject,
        message,
        utm_source: utmSource || "",
        utm_campaign: utmCampaign || "",
        utm_content: utmContent || "",
        utm_term: utmTerm || "",
        utm_medium: utmMedium || "",
        gclid: gclid || "",
        type: "Agence",
        priority: "Mineur",
        encoding: "UTF-8",

        // Consentements
        consent_gdpr: consentGdpr,
        consent_email: consentEmail,

        // Données d'adresse détaillées pour Strapi (champs normalisés)
        street: street,
        postcode: postcode,
        city: city,
        address: addressQuery, // Adresse brute saisie (utilisée pour l'email de notification)
        AccountID: idSalesforce, // idSalesforce correspond à AccountID dans Strapi

        // Champs Salesforce (noms de champs spécifiques à Salesforce)
        ...(FORM_FIELDS.LASTNAME && { [FORM_FIELDS.LASTNAME]: lastname }),
        ...(FORM_FIELDS.FIRSTNAME && { [FORM_FIELDS.FIRSTNAME]: firstname }),
        ...(FORM_FIELDS.STREET && { [FORM_FIELDS.STREET]: street }),
        ...(FORM_FIELDS.CITY && { [FORM_FIELDS.CITY]: city }),
        ...(FORM_FIELDS.POSTCODE && { [FORM_FIELDS.POSTCODE]: postcode }),
        ...(FORM_FIELDS.ACCOUNT_ID && { [FORM_FIELDS.ACCOUNT_ID]: idSalesforce }),
        ...(FORM_FIELDS.ACQUISITION_CHANNEL && { [FORM_FIELDS.ACQUISITION_CHANNEL]: "Formulaire" }),
        ...(FORM_FIELDS.PHONE_INDICATOR && { [FORM_FIELDS.PHONE_INDICATOR]: "FR" }),
        acquisition_channel: "Formulaire", // Champ normalisé pour le backend
        phone_indicator: "FR", // Indicatif téléphonique par défaut
      },
    };

    // DEBUG: Log du payload complet envoyé au backend
    console.log('📤 PAYLOAD COMPLET ENVOYÉ AU BACKEND (Contact):');
    console.log(JSON.stringify(submissionData, null, 2));

    // DEBUG: Vérifier les valeurs des champs cachés
    console.log('🔍 VALEURS CHAMPS CACHÉS AVANT SOUMISSION:');
    console.log('  - parsedAddress.street:', parsedAddress.street);
    console.log('  - parsedAddress.city:', parsedAddress.city);
    console.log('  - parsedAddress.postcode:', parsedAddress.postcode);

    try {
      // Send to Strapi API (which will handle both Strapi save and Salesforce submission)
      // Utiliser toujours le endpoint /submissions avec le bon type
      const endpoint = process.env.NEXT_PUBLIC_SUBMISSIONS_ENDPOINT;
      const url = new URL(endpoint);

      console.log('📍 Endpoint utilisé:', endpoint, '(type:', contactType, ')');

      const response = await fetch(url.href, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(submissionData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.error?.message || "Erreur lors de l'envoi du formulaire"
        );
      }

      const result = await response.json();

      // Check if Salesforce submission was successful
      if (result.meta?.salesforceSubmitted === false) {
      }

      // L'email de notification est envoyé côté back (sendMailWp) par le endpoint /submissions
      setSubmitStatus("success");
      form.reset();

      // Réinitialiser les états d'adresse et les erreurs de validation
      clearErrors();
      setGdprChecked(false);
      setShowIdelioBox(false);
      setCallbackChecked(false);
      setAddressQuery("");
      setSelectedPlaceId("");
      setAddressDetails({
        street: "",
        postcode: "",
        city: "",
        idSalesforce: "",
      });
      setPredictions([]);
      setShowPredictions(false);
    } catch (error) {
      console.error("Submission error:", error);
      setSubmitStatus("error");
      setErrorMessage(
        error instanceof Error ? error.message : "Une erreur est survenue"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className={cn("w-full bg-muted py-16", className)} key={id} id="contact">
      <div className="mx-auto w-full max-w-5xl lms-padding-mobile">
        <header className="mb-8 space-y-2 text-center">
          <Heading as="h2" className="text-4xl md:text-5xl font-bold text-text">{data.titre}</Heading>
        </header>

        <form className="space-y-8" onSubmit={handleSubmit} noValidate>
          <div className="grid gap-6 md:grid-cols-2">
            <div className="space-y-2">
              <Input
                id="lastname"
                name="lastname"
                placeholder="Nom"
                required
                iconName={FIELD_ICONS.nom}
                error={errors.lastname}
                onChange={(e) => validateField("lastname", e.target.value)}
                onBlur={(e) => validateField("lastname", e.target.value)}
              />
              {/* Champs cachés pour Salesforce */}
              <input
                type="hidden"
                name={FORM_FIELDS.ORGID}
                value={process.env.NEXT_PUBLIC_SF_ORG_ID_ID}
              />
              <input
                type="hidden"
                name={FORM_FIELDS.RECORD_TYPE}
                value={process.env.NEXT_PUBLIC_SF_RECORD_TYPE_ID}
              />
              <input type="hidden" name={FORM_FIELDS.SUBJECT} value={subject} />
              <input
                type="hidden"
                name={FORM_FIELDS.TYPE}
                value={process.env.NEXT_PUBLIC_SF_RECORD_TYPE_ID}
              />
              <input
                type="hidden"
                name={FORM_FIELDS.PRIORITY}
                value={process.env.NEXT_PUBLIC_SF_PRIORITY_ID}
              />
              <input type="hidden" name={FORM_FIELDS.ENCODING} value="UTF-8" />
              {/* Champs d'adresse cachés - utiliser les valeurs parsées */}
              <input
                type="hidden"
                name={FORM_FIELDS.STREET}
                value={parsedAddress.street}
              />
              <input
                type="hidden"
                name={FORM_FIELDS.CITY}
                value={parsedAddress.city}
              />
              <input
                type="hidden"
                name={FORM_FIELDS.POSTCODE}
                value={parsedAddress.postcode}
              />
              <input
                type="hidden"
                name={FORM_FIELDS.ACCOUNT_ID}
                value={addressDetails.idSalesforce}
              />
              <input
                type="hidden"
                name={FORM_FIELDS.PHONE_INDICATOR}
                value="FR"
              />
            </div>

            <div className="space-y-2">
              <Input
                id="firstname"
                name="firstname"
                placeholder="Prénom"
                required
                iconName={FIELD_ICONS.prenom}
                error={errors.firstname}
                onChange={(e) => validateField("firstname", e.target.value)}
                onBlur={(e) => validateField("firstname", e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Input
                id={FORM_FIELDS.EMAIL}
                type="email"
                name={FORM_FIELDS.EMAIL}
                placeholder="Email"
                required
                iconName={FIELD_ICONS.email}
                error={errors.email}
                onChange={(e) => validateField("email", e.target.value)}
                onBlur={(e) => validateField("email", e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Input
                id={FORM_FIELDS.PHONE}
                type="tel"
                name={FORM_FIELDS.PHONE}
                placeholder="Téléphone"
                iconName={FIELD_ICONS.telephone}
                error={errors.phone}
                onChange={(e) => validateField("phone", e.target.value)}
                onBlur={(e) => validateField("phone", e.target.value)}
              />
            </div>

            <div className="space-y-2 md:col-span-2 relative">
              <Input
                id={FORM_FIELDS.ADDRESS}
                name={FORM_FIELDS.ADDRESS}
                placeholder="Adresse de livraison"
                required
                iconName={FIELD_ICONS.adresse}
                value={addressQuery}
                error={errors.adresse}
                onChange={(e) => {
                  setAddressQuery(e.target.value);
                  validateField("adresse", e.target.value);
                  // Nouvelle saisie : l'agence précédemment résolue ne correspond plus
                  setShowIdelioBox(false);
                  setCallbackChecked(false);
                }}
                onFocus={() =>
                  predictions.length > 0 && setShowPredictions(true)
                }
                onBlur={(e) => {
                  validateField("adresse", e.target.value);
                  setTimeout(() => setShowPredictions(false), 300);
                }}
                autoComplete="off"
              />
              {showPredictions && predictions.length > 0 && (
                <ul
                  className="absolute z-10 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto mt-1"
                  onMouseDown={(e) => {
                    e.preventDefault();
                  }}
                >
                  {predictions.map((prediction) => {
                    const placeId = prediction.placeId;
                    return (
                      <li
                        key={placeId}
                        role="button"
                        tabIndex={0}
                        className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                        onMouseDown={async (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setShowPredictions(false);
                          setAddressQuery(prediction.description);
                          validateField("adresse", prediction.description);
                          setSelectedPlaceId(placeId);
                          setIsLoadingAddress(true);

                          try {
                            const data = await geoService.getMap(placeId);
                            console.log('🔍 API getMap - Réponse complète:', data);

                            const fullDescription = prediction.description;
                            console.log('🔍 Adresse client à parser:', fullDescription);

                            const addressPattern = /^(.+?)\s*,?\s*(\d{5})\s+(.+)$/;
                            const match = fullDescription.match(addressPattern);

                            let extractedStreet = '';
                            let extractedPostcode = '';
                            let extractedCity = '';

                            if (match) {
                              extractedStreet = match[1].trim().replace(/,\s*$/, '');
                              extractedPostcode = match[2];
                              extractedCity = match[3].split(',')[0].trim();
                              console.log('✅ Parsing réussi - street:', extractedStreet, '| postcode:', extractedPostcode, '| city:', extractedCity);
                            } else {
                              console.log('⚠️ Pattern non trouvé, appel geocoding API...');
                              try {
                                const response = await fetch(`https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(fullDescription)}&limit=1`);
                                const geocodingData = await response.json();
                                if (geocodingData.features && geocodingData.features.length > 0) {
                                  const feature = geocodingData.features[0].properties;
                                  extractedStreet = feature.name || '';
                                  extractedPostcode = feature.postcode || '';
                                  extractedCity = feature.city || '';
                                  console.log('✅ Geocoding réussi - street:', extractedStreet, '| postcode:', extractedPostcode, '| city:', extractedCity);
                                }
                              } catch (error) {
                                console.warn('⚠️ Erreur geocoding:', error);
                                extractedStreet = fullDescription;
                              }
                            }

                            const agencyDetails = await getAgencyDetails(placeId);
                            const idSalesforce = agencyDetails?.idSalesforce || "";

                            // L'agence propose l'appel immédiat : on la persiste
                            // pour la popup CallBack et on affiche l'encart vert
                            const idelioAllowed =
                              agencyDetails?.showIdelioButton === true &&
                              !!agencyDetails?.id;

                            setShowIdelioBox(idelioAllowed);
                            setCallbackChecked(false);
                            if (idelioAllowed && agencyDetails) {
                              setAgency({
                                ...agencyDetails,
                                phoneIdelio: agencyDetails.phoneIdelio || "",
                              });
                              setHasSelected(true);
                            }

                            setAddressDetails({
                              street: extractedStreet,
                              postcode: extractedPostcode,
                              city: extractedCity,
                              idSalesforce,
                            });

                            setIsLoadingAddress(false);
                          } catch {
                            setIsLoadingAddress(false);
                          }
                        }}
                      >
                        {prediction.description}
                      </li>
                    );
                  })}
                </ul>
              )}

              {/* Encart vert : l'agence de l'adresse propose l'appel immédiat */}
              {showIdelioBox && (
                <div className="mt-3 flex items-center gap-5 rounded-xl border border-primaryGreen/40 bg-secondaryGreen px-6 py-5">
                  {/* Icône téléphone dans un cercle vert */}
                  <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-accent">
                    <Phone size={26} className="text-white" />
                  </span>

                  {/* Texte */}
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                      <p className="text-lg font-bold text-dark">
                        Oui, rappelez-moi tout de suite !
                      </p>
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-accent px-3 py-1 text-xs font-bold uppercase text-white">
                        <span className="h-1.5 w-1.5 rounded-full bg-white" />
                        En ligne
                      </span>
                    </div>
                    <p className="mt-1.5 text-base text-textColor">
                      Cochez cette case et envoyez le formulaire : un conseiller
                      vous rappelle en quelques secondes au numéro indiqué —
                      gratuit, sans engagement.
                    </p>
                  </div>

                  {/* Checkbox custom (bord verte, fond blanc) */}
                  <span className="relative inline-flex shrink-0">
                    <input
                      type="checkbox"
                      id="idelio-callback"
                      aria-label="Être rappelé immédiatement par un conseiller"
                      checked={callbackChecked}
                      onChange={(e) => {
                        setCallbackChecked(e.target.checked);
                        if (e.target.checked) setShowCallPopup(true);
                      }}
                      className="peer h-7 w-7 cursor-pointer appearance-none rounded-md border-2 border-accent bg-white transition-colors checked:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50"
                    />
                    <svg
                      viewBox="0 0 20 20"
                      fill="none"
                      aria-hidden="true"
                      className="pointer-events-none absolute inset-0 m-auto h-4 w-4 text-white opacity-0 transition-opacity peer-checked:opacity-100"
                    >
                      <path
                        d="M4 10.5l4 4 8-9"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                </div>
              )}
            </div>

            <div className="space-y-2 md:col-span-2">
              <Textarea
                id={FORM_FIELDS.MESSAGE}
                name={FORM_FIELDS.MESSAGE}
                placeholder="Message"
                required
                iconName={FIELD_ICONS.message}
                error={errors.description}
                onChange={(e) => validateField("description", e.target.value)}
                onBlur={(e) => validateField("description", e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-6">
            <Checkbox
              id={FORM_FIELDS.CONSENT_GDPR}
              name={FORM_FIELDS.CONSENT_GDPR}
              label="J'accepte que mes données soient utilisées pour traiter ma demande conformément à la politique de confidentialité."
              required
              checked={gdprChecked}
              onChange={(e) => {
                setGdprChecked(e.target.checked);
                validateField("consent_gdpr", e.target.checked);
              }}
              error={errors.consent_gdpr}
            />

            <div className="space-y-4">


              <div className="flex flex-col gap-4">
                <Checkbox
                  id={FORM_FIELDS.CONSENT_EMAIL}
                  name={FORM_FIELDS.CONSENT_EMAIL}
                  label="Pour pouvoir vous contacter et répondre rapidement à votre demande, merci de valider votre autorisation de contact par e-mail."
                  required
                  onChange={(e) =>
                    validateField("consent_email", e.target.checked)
                  }
                  error={errors.consent_email}
                />
              </div>
            </div>
          </div>

          <div className="flex justify-center">
            <Button
              type="submit"
              variant="default"
              size="lg"
              disabled={isSubmitting}
              className="h-14 px-12 text-lg font-semibold uppercase text-white bg-secondary hover:bg-primaryGreen disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? "Envoi en cours..." : "envoyer"}
            </Button>
          </div>

          {/* Success message */}
          {submitStatus === "success" && (
            <div className="p-4 text-sm text-green-700 bg-green-100 rounded-lg">
               Votre message a été envoyé avec succès. Nous vous recontacterons rapidement.
            </div>
          )}

          {/* Error message */}
          {submitStatus === "error" && errorMessage && (
            <div className="p-4 text-sm text-red-700 bg-red-100 rounded-lg">
              ❌ {errorMessage}
            </div>
          )}
        </form>
      </div>

      {/* Popup webcallback (appel immédiat) */}
      {showCallPopup && (
        <CallPopup
          adresse={addressQuery}
          onClose={() => {
            setShowCallPopup(false);
            setCallbackChecked(false);
          }}
        />
      )}
    </section>
  );
}
